/* eslint-disable @typescript-eslint/no-explicit-any */
import { NOT_APPLICABLE } from '@config/constants/common';
import { apiClient } from '@lib/api-client';
import { useQuery } from '@tanstack/react-query';
import { getCountryName } from '@utils/countries';
import {
	formatCurrency,
	formatNumberWithSpaces,
	formatTokenAvailable,
} from '@utils/formats';
import { convertGCO2EToTCO2EWithoutUnit } from '@utils/functions';
import { getTechnologyProject } from '@utils/project-technologies';
import { useTranslation } from 'react-i18next';
import { ApiResponse } from '../interfaces';

export const getMesurabilityProjects = (
	params: any
): Promise<ApiResponse> => {
	return apiClient.get('/measurabilities', {
		params: {
			page: params.page,
			pageSize: params.pageSize,
			query: params.queryParams,
			sort: '-quantity.remaining_aco2 name',
			populate:
				'cover, logo, publicSpecs, supportedBy.logo',
		},
	});
};

export const useMesurabilityProjects = (
	page: number,
	size: number,
	queryParams: any
) => {
	const { t } = useTranslation();
	const { data, isFetching, isFetched, isRefetching } =
		useQuery({
			queryKey: [
				'mesurability-projects-offsets',
				{
					page: page,
					size: size,
					queryParams: queryParams,
				},
			],
			queryFn: () =>
				getMesurabilityProjects({
					page: page,
					pageSize: size,
					queryParams: queryParams,
				}),
			staleTime: 1000,
		});

	const updateList = (projects: any) =>
		projects.reduce((acc: any, currentItem: any) => {
			const technolgyProject = getTechnologyProject(
				currentItem.technology
			);
			const countryName = getCountryName(
				currentItem.location.countryCode
			);

			const cover =
				currentItem && currentItem.cover
					? currentItem.cover
					: technolgyProject
						? technolgyProject.image
						: '';

			const modifiedItem = {
				id: currentItem.id,
				name: currentItem.name,
				country: {
					name: countryName,
					code:
						currentItem.location &&
						currentItem.location.countryCode
							? currentItem.location.countryCode
							: NOT_APPLICABLE,
				},
				region:
					currentItem.location &&
					currentItem.location.place
						? currentItem.location.place
						: NOT_APPLICABLE,
				type: technolgyProject
					? technolgyProject.label
					: NOT_APPLICABLE,
				priceTco2: formatCurrency(
					currentItem.sellPrice_europertco2eq,
					t('global.sellPriceUnit')
				),

				tokenAvailable: formatTokenAvailable(
					convertGCO2EToTCO2EWithoutUnit(
						currentItem.quantity.remaining_aco2
					)
				),
				image: cover,
				quantitytco2eq: 0,
				supportedBy: currentItem?.supportedBy?.logo,
				sellPrice_europertco2eq: formatNumberWithSpaces(
					currentItem.sellPrice_europertco2eq
				),
				isDisabled:
					formatTokenAvailable(
						convertGCO2EToTCO2EWithoutUnit(
							currentItem.quantity.remaining_aco2
						)
					) == '0',
				active: currentItem?.active,
			};
			acc.push(modifiedItem);

			return acc;
		}, []);

	return {
		data: data ? updateList(data?.rows) : [],
		isLoading: isFetching && !isFetched,
		isRefetching,
		isFetching,
		isFetched,
		total: data?.total,
		page: data?.page,
		pageSize: data?.pageSize,
		totalPages: data?.totalPages,
	};
};
